'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { Icon, isIconName } from '../shared/Icon';
import { headingLevel, items, text, type HeadingLevel, type Localized } from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';
import { homeSurfaceClass, HomeSurfaceStyles, type HomeSurface } from './homeShared';

/**
 * A band of up to four selling points, each an icon in a soft green square, a
 * short title and a line of text.
 *
 * <p>Every point is a claim about the business (sizes, samples, delivery, a
 * guarantee), so the block ships none: the owner writes them in the template.
 * The section heading is visually hidden but always present, so the band is a
 * landmark a screen-reader user can jump to.
 *
 * <p>Four columns from 1101 px, two from 601 px, one below.
 */
export interface SpatialKitchenUsp {
  icon?: string;
  title?: Localized;
  body?: Localized;
}

export interface SpatialKitchenUspStripProps {
  /** Visually hidden section heading. */
  title?: Localized;
  items?: SpatialKitchenUsp[];
  surface?: HomeSurface;
  headingLevel?: HeadingLevel;
}

const CSS = `
.sk-usps{padding-block:40px}
@media (min-width:769px){.sk-usps{padding-block:48px}}
.sk-usps__grid{display:grid;gap:22px}
@media (min-width:601px){.sk-usps__grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (min-width:1101px){.sk-usps__grid--3{grid-template-columns:repeat(3,minmax(0,1fr))}.sk-usps__grid--4{grid-template-columns:repeat(4,minmax(0,1fr))}}
.sk-usp{display:flex;gap:14px;align-items:flex-start;min-width:0}
.sk-usp__icon{flex:none;display:grid;place-items:center;width:44px;height:44px;border-radius:12px;background:var(--sk-sage-grey);color:var(--sk-sage)}
.sk-usp__title{display:block;margin-bottom:2px;font-weight:700;color:var(--sk-ink)}
.sk-usp__body{margin:0;font-size:.9rem;line-height:1.55;color:var(--sk-muted)}
`;

export function SpatialKitchenUspStrip(props: SpatialKitchenUspStripProps) {
  const usps = items<SpatialKitchenUsp>(props.items)
    .filter((usp) => text(usp.title) || text(usp.body))
    .slice(0, 4);
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const titleId = useId();
  const columns = usps.length >= 4 ? 4 : usps.length === 3 ? 3 : 2;

  return (
    <BlockRoot
      as="section"
      className={`sk sk-usps ${homeSurfaceClass(props.surface, 'stone')}`}
      aria-labelledby={titleId}
    >
      <SkBaseStyles />
      <HomeSurfaceStyles />
      <SkStyles id="usp-strip" css={CSS} />
      <div className="sk-container">
        <Title id={titleId} className="sk-visually-hidden">
          {text(props.title, 'Why shop with us')}
        </Title>
        <ul className={`sk-usps__grid sk-usps__grid--${columns} sk-list-reset`}>
          {usps.map((usp, index) => (
            <li key={`${text(usp.title)}-${index}`} className="sk-usp">
              <span className="sk-usp__icon">
                <Icon name={isIconName(usp.icon) ? usp.icon : 'check'} size={22} />
              </span>
              <div>
                {text(usp.title) && <strong className="sk-usp__title">{text(usp.title)}</strong>}
                {text(usp.body) && <p className="sk-usp__body">{text(usp.body)}</p>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </BlockRoot>
  );
}
